import { useEffect, useState } from 'react';

import { getLabelUsageData } from '../../../../../../element/labels/labelsService';

export type LabelUsageDataState = {
    status: 'fetching' | 'success' | 'error';
    count: number;
};

/*
 * Custom hook to fetch and manage the usage data of a label. Use this hook at the level of the component that needs the usage data,
 * and pass the returned state down to any child components that need to display it.
 * This ensures that the data is fetched only once per label and shared across all relevant components.
 */
export const useLabelUsageData = (labelId: string | null): LabelUsageDataState => {
    const [usageDataState, setUsageDataState] = useState<LabelUsageDataState>({ status: 'fetching', count: 0 });

    useEffect(() => {
        if (!labelId) {
            setUsageDataState({ status: 'fetching', count: 0 });
            return;
        }

        let isCurrent = true;

        const getLabelUsage = async () => {
            setUsageDataState({ status: 'fetching', count: 0 });

            try {
                const labelUsageData = await getLabelUsageData(labelId);
                if (isCurrent) setUsageDataState({ status: 'success', count: labelUsageData.length });
            } catch (e) {
                if (isCurrent) setUsageDataState({ status: 'error', count: 0 });
            }
        };

        getLabelUsage();

        return () => {
            isCurrent = false;
        };
    }, [labelId]);

    return usageDataState;
};
